import apiClient from './api';
import * as XLSX from 'xlsx';
import { API_ENDPOINTS } from '@/config/api.config';
import type {
  LearningPathPerformanceMetricsResponse,
  LearningPathPerformanceListResponse,
  LearningPathPerformanceResponse,
  LearningPathPerformanceListParams,
  LearningPathPerformanceMetricsParams,
  LearningPathPerformanceMetricsDto,
  LearningPathPerformanceListResponseDto,
  LearningPathPerformanceItemDto,
  Granularity,
  LearningPathUsersListParams,
  LearningPathDataResponse,
  LearningPathDataResponses,
  LearningPathUserData,
  LearningPathUserCoursesResponses,
  LearningPathUserCoursesResponse,
  LearningPathDetailResponses,
  LearningPathDetailResponse,
  LearningPathUsersProgressResponses,
  LearningPathUsersProgressResponse,
  UserLearningPathsResponse,
  UserAssignedLearningPath,
  LearningPathFullDetailsResponse,
  LearningPathFullDetails
} from '@/types/learning-path-performance.types';

/**
 * Service for managing learning path performance analytics
 * Provides metrics, list views, and detailed learning path performance data
 */
class LearningPathPerformanceService {
  /**
   * Get high-level learning path performance metrics
   * Returns total active learning paths, enrollments, and average completion rate
   * @param params - Optional granularity parameter for time-based filtering
   * @returns Promise with learning path performance metrics
   */
  async getMetrics(params?: LearningPathPerformanceMetricsParams): Promise<LearningPathPerformanceMetricsDto> {
    try {
      const body: any = {};

      if (params?.granularity) {
        body.granularity = params.granularity;
      }
      if (params?.start_date) {
        body.start_date = params.start_date;
      }
      if (params?.end_date) {
        body.end_date = params.end_date;
      }

      const response = await apiClient.post<LearningPathPerformanceMetricsResponse>(
        API_ENDPOINTS.LEARNING_PATH_PERFORMANCE.METRICS,
        body
      );

      if (!response.data.success) {
        throw new Error(response.data.description || 'Failed to fetch learning path performance metrics');
      }

      return response.data.data;
    } catch (error) {
      console.error('Error fetching learning path performance metrics:', error);
      throw error;
    }
  }

  /**
   * Get paginated list of learning path performance data with filtering and sorting
   * @param params - Query parameters for filtering, sorting, and pagination
   * @returns Promise with paginated learning path performance list
   */
  async getLearningPathPerformanceList(params?: LearningPathPerformanceListParams): Promise<LearningPathPerformanceListResponseDto> {
    try {
      const body: any = {};

      // Add parameters to body if provided
      if (params?.page !== undefined) {
        body.page = params.page;
      }
      if (params?.limit !== undefined) {
        body.limit = params.limit;
      }
      if (params?.status) {
        body.status = params.status;
      }
      if (params?.search) {
        body.search = params.search;
      }
      if (params?.sortBy) {
        body.sortBy = params.sortBy;
      }
      if (params?.sortOrder) {
        body.sortOrder = params.sortOrder;
      }
      if (params?.granularity) {
        body.granularity = params.granularity;
      }
      if (params?.start_date) {
        body.start_date = params.start_date;
      }
      if (params?.end_date) {
        body.end_date = params.end_date;
      }
      if (params?.groupKey) {
        body.groupKey = params.groupKey;
      }
      if (params?.roleKey) {
        body.roleKey = params.roleKey;
      }
      if (params?.categoryId) {
        body.categoryId = params.categoryId;
      }

      const response = await apiClient.post<LearningPathPerformanceListResponse>(
        API_ENDPOINTS.LEARNING_PATH_PERFORMANCE.LIST,
        body
      );

      if (!response.data.success) {
        throw new Error(response.data.description || 'Failed to fetch learning path performance list');
      }

      return response.data.data;
    } catch (error) {
      console.error('Error fetching learning path performance list:', error);
      throw error;
    }
  }

  /**
   * Get users enrolled in a specific learning path
   * @param learningPathId - The unique identifier of the learning path
   * @param params - Query parameters for pagination and search
   * @returns Promise with learning path users data
   */
  async getLearningPathUsers(learningPathId: string, params: LearningPathUsersListParams): Promise<LearningPathDataResponses> {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.search) queryParams.append('search', params.search);
      if (params.status) queryParams.append('status', params.status);

      const queryString = queryParams.toString();
      const url = `${API_ENDPOINTS.LEARNING_PATH_PERFORMANCE.GET_USER_BASED_ON_LEARNING_PATH(learningPathId)}${queryString ? `?${queryString}` : ''}`;
      const response = await apiClient.get<LearningPathDataResponses>(url);

      if (!response.data.success) {
        throw new Error(response.data.description || 'Failed to fetch learning path users');
      }

      return response.data;
    } catch (error) {
      console.error(`Error fetching users for learning path ID ${learningPathId}:`, error);
      throw error;
    }
  }

  /**
   * Get detailed learning path with all courses
   * @param learningPathId - The unique identifier of the learning path
   * @returns Promise with learning path details and courses
   */
  async getLearningPathDetail(learningPathId: string): Promise<LearningPathDetailResponse | null> {
    try {
      const url = API_ENDPOINTS.LEARNING_PATH_PERFORMANCE.GET_BY_ID(learningPathId);
      const response = await apiClient.get<LearningPathDetailResponses>(url);

      if (!response.data.success) {
        if (response.data.code === 404) {
          return null;
        }
        throw new Error(response.data.description || 'Failed to fetch learning path details');
      }

      return response.data.data;
    } catch (error) {
      console.error(`Error fetching learning path details for ID ${learningPathId}:`, error);
      throw error;
    }
  }

  /**
   * Get all users assigned to a learning path with their progress in each course
   * @param learningPathId - The unique identifier of the learning path
   * @returns Promise with users progress data
   */
  async getUsersProgress(
    learningPathId: string,
    params?: { page?: number; limit?: number; search?: string; status_filter?: string }
  ): Promise<LearningPathUsersProgressResponses> {
    try {
      const url = API_ENDPOINTS.LEARNING_PATH_PERFORMANCE.GET_USERS_PROGRESS(learningPathId);
      const body: any = {};
      if (params?.page) body.page = params.page;
      if (params?.limit) body.limit = params.limit;
      if (params?.search) body.search = params.search;
      if (params?.status_filter) body.status_filter = params.status_filter;

      const response = await apiClient.post<LearningPathUsersProgressResponses>(url, body);

      if (!response.data.success) {
        throw new Error(response.data.description || 'Failed to fetch users progress');
      }

      return response.data;
    } catch (error) {
      console.error(`Error fetching users progress for learning path ${learningPathId}:`, error);
      throw error;
    }
  }

  /**
   * Get learning path user analytics (metrics)
   */
  async getUserAnalytics(learningPathId: string): Promise<{ total_users: number; completed_count: number; average_progress: number }> {
    const url = API_ENDPOINTS.LEARNING_PATH_PERFORMANCE.GET_USERS_ANALYTICS(learningPathId);
    const response = await apiClient.get(url);
    return response.data?.data;
  }

  /**
   * Export learning path user data using dedicated export API (no pagination)
   */
  async exportLearningPathUserData(
    learningPathId: string,
    format: 'csv' | 'excel',
    filters?: { search?: string; status_filter?: string; learningPathName?: string }
  ): Promise<{ success: boolean; filename: string; count: number; message?: string }> {
    const body: any = {};
    if (filters?.search) body.search = filters.search;
    if (filters?.status_filter) body.status_filter = filters.status_filter;

    const url = API_ENDPOINTS.EXPORT.LEARNING_PATH_USER_DATA(learningPathId);
    const response = await apiClient.post(url, body);

    const data = response.data?.data;
    const users = data?.users || [];

    if (users.length === 0) {
      return {
        success: false,
        filename: '',
        count: 0,
        message: 'No user data available to export with the current filters'
      };
    }

    const filename = this.generateLPUserDataFilename(format, filters);

    if (format === 'excel') {
      const blob = this.generateLPUserDataExcel(users);
      this.downloadBlob(blob, filename);
    } else {
      const csv = this.generateLPUserDataCSV(users);
      this.downloadCSVContent(csv, filename);
    }

    return { success: true, filename, count: users.length };
  }

  private formatSecondsToHHMMSS(seconds: number): string {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }

  private generateLPUserDataFilename(
    format: 'csv' | 'excel',
    filters?: { search?: string; status_filter?: string; learningPathName?: string }
  ): string {
    const ext = format === 'excel' ? 'xlsx' : 'csv';
    const parts = ['learning-path-users'];
    if (filters?.learningPathName) {
      parts.push(filters.learningPathName.replace(/[^a-z0-9]/gi, '-').toLowerCase());
    }
    if (filters?.status_filter) {
      parts.push(filters.status_filter.toLowerCase());
    }
    if (filters?.search) {
      parts.push(`search-${filters.search.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`);
    }
    parts.push(new Date().toISOString().split('T')[0]);
    return parts.join('_') + `.${ext}`;
  }

  private generateLPUserDataCSV(users: any[]): string {
    const headers = ['First Name', 'Last Name', 'Email', 'Status', 'Progress (%)', 'Time Spent (HH:MM:SS)'];
    const escape = (val: string | number) => {
      const s = String(val);
      return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = users.map((u: any) => [
      escape(u.given_name),
      escape(u.family_name),
      escape(u.email),
      escape(u.status.replace(/_/g, ' ')),
      escape(u.progress_percentage),
      escape(this.formatSecondsToHHMMSS(u.total_time_spent_seconds)),
    ].join(','));
    return [headers.join(','), ...rows].join('\n');
  }

  private generateLPUserDataExcel(users: any[]): Blob {
    const data = users.map((u: any) => ({
      'First Name': u.given_name,
      'Last Name': u.family_name,
      'Email': u.email,
      'Status': u.status.replace(/_/g, ' '),
      'Progress (%)': u.progress_percentage,
      'Time Spent (HH:MM:SS)': this.formatSecondsToHHMMSS(u.total_time_spent_seconds),
    }));
    const worksheet = XLSX.utils.json_to_sheet(data);
    worksheet['!cols'] = [
      { wch: 15 }, { wch: 15 }, { wch: 30 }, { wch: 15 }, { wch: 12 }, { wch: 20 },
    ];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Learning Path Users');
    const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    return new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  }

  private downloadCSVContent(csv: string, filename: string) {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    this.downloadBlob(blob, filename);
  }

  private downloadBlob(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Get all learning paths assigned to a specific user
   * @param userId - The unique identifier of the user
   * @returns Promise with user's assigned learning paths
   */
  async getUserLearningPaths(userId: string): Promise<UserAssignedLearningPath[]> {
    try {
      const url = API_ENDPOINTS.LEARNING_PATH_PERFORMANCE.GET_USER_LEARNING_PATHS(userId);
      const response = await apiClient.get<UserLearningPathsResponse>(url);

      if (!response.data.success) {
        throw new Error(response.data.description || 'Failed to fetch user learning paths');
      }

      return response.data.data;
    } catch (error) {
      console.error(`Error fetching learning paths for user ${userId}:`, error);
      throw error;
    }
  }

  /**
   * Get a specific user's course progress within a learning path
   * @param learningPathId - The unique identifier of the learning path
   * @param userId - The unique identifier of the user
   * @returns Promise with user's course progress data
   */
  async getUserCourses(learningPathId: string, userId: string): Promise<LearningPathUserCoursesResponses> {
    try {
      const url = API_ENDPOINTS.LEARNING_PATH_PERFORMANCE.GET_USER_COURSES(learningPathId, userId);
      const response = await apiClient.get<LearningPathUserCoursesResponses>(url);

      if (!response.data.success) {
        throw new Error(response.data.description || 'Failed to fetch user courses');
      }

      return response.data;
    } catch (error) {
      console.error(`Error fetching courses for user ${userId} in learning path ${learningPathId}:`, error);
      throw error;
    }
  }

  /**
   * Get full learning path details with courses and modules for a specific user
   * @param learningPathId - The unique identifier of the learning path
   * @param userId - The unique identifier of the user
   * @returns Promise with full learning path details including courses and modules
   */
  async getLearningPathFullDetails(learningPathId: string, userId: string): Promise<LearningPathFullDetails> {
    try {
      const url = API_ENDPOINTS.LEARNING_PATH_PERFORMANCE.GET_FULL_DETAILS(learningPathId, userId);
      const response = await apiClient.get<LearningPathFullDetailsResponse>(url);

      if (!response.data.success) {
        throw new Error(response.data.description || 'Failed to fetch learning path full details');
      }

      return response.data.data;
    } catch (error) {
      console.error(`Error fetching full details for learning path ${learningPathId} and user ${userId}:`, error);
      throw error;
    }
  }

  /**
   * Get performance data for a specific learning path
   * @param learningPathId - The unique identifier of the learning path
   * @param granularity - Optional time granularity for filtering
   * @returns Promise with learning path performance data or null if not found
   */
  async getLearningPathPerformanceById(learningPathId: string, granularity?: Granularity): Promise<LearningPathPerformanceItemDto | null> {
    try {
      const queryParams = granularity ? `?granularity=${granularity}` : '';
      const response = await apiClient.get<LearningPathPerformanceResponse>(
        `${API_ENDPOINTS.LEARNING_PATH_PERFORMANCE.GET_BY_ID(learningPathId)}${queryParams}`
      );

      if (!response.data.success) {
        // Check if it's a 404 (learning path not found)
        if (response.data.code === 404) {
          return null;
        }
        throw new Error(response.data.description || 'Failed to fetch learning path performance');
      }

      return response.data.data;
    } catch (error) {
      console.error(`Error fetching learning path performance for ID ${learningPathId}:`, error);
      throw error;
    }
  }

  /**
   * Search learning paths by title
   * @param searchTerm - The search term to filter learning paths by title
   * @param limit - Maximum number of results to return
   * @returns Promise with filtered learning path list
   */
  async searchLearningPaths(searchTerm: string, limit: number = 20): Promise<LearningPathPerformanceListResponseDto> {
    return this.getLearningPathPerformanceList({
      search: searchTerm,
      limit,
      page: 1,
      sortBy: 'name',
      sortOrder: 'asc'
    });
  }

  /**
   * Get top performing learning paths by completion rate
   * @param limit - Number of top learning paths to return
   * @returns Promise with top performing learning paths
   */
  async getTopPerformingLearningPaths(limit: number = 10): Promise<LearningPathPerformanceItemDto[]> {
    const result = await this.getLearningPathPerformanceList({
      status: 'active',
      sortBy: 'completion',
      sortOrder: 'desc',
      limit,
      page: 1
    });

    return result.learning_paths;
  }

  /**
   * Get learning paths with low completion rates that may need attention
   * @param threshold - Completion percentage threshold (default 50%)
   * @param limit - Maximum number of learning paths to return
   * @returns Promise with learning paths needing attention
   */
  async getLearningPathsNeedingAttention(threshold: number = 50, limit: number = 20): Promise<LearningPathPerformanceItemDto[]> {
    const result = await this.getLearningPathPerformanceList({
      status: 'active',
      sortBy: 'completion',
      sortOrder: 'asc',
      limit,
      page: 1
    });

    // Filter learning paths below the threshold
    return result.learning_paths.filter(path => path.completion_percentage < threshold);
  }

  /**
   * Get most popular learning paths by enrollment
   * @param limit - Number of learning paths to return
   * @returns Promise with most enrolled learning paths
   */
  async getMostEnrolledLearningPaths(limit: number = 10): Promise<LearningPathPerformanceItemDto[]> {
    const result = await this.getLearningPathPerformanceList({
      status: 'active',
      sortBy: 'enrollments',
      sortOrder: 'desc',
      limit,
      page: 1
    });

    return result.learning_paths;
  }

  /**
   * Get learning paths by status
   * @param status - Learning path status to filter by
   * @param page - Page number for pagination
   * @param limit - Items per page
   * @returns Promise with filtered learning path list
   */
  async getLearningPathsByStatus(
    status: 'active' | 'inactive' | 'completed' | 'draft',
    page: number = 1,
    limit: number = 20
  ): Promise<LearningPathPerformanceListResponseDto> {
    return this.getLearningPathPerformanceList({
      status,
      page,
      limit,
      sortBy: 'name',
      sortOrder: 'asc'
    });
  }

  /**
   * Export learning path performance data to CSV format
   * @param format - Export format ('csv' or 'excel')
   * @param filters - Optional filters to apply
   * @returns Promise with export data
   */
  async exportLearningPathPerformance(
    format: 'csv' | 'excel' = 'csv',
    filters?: LearningPathPerformanceListParams
  ): Promise<Blob> {
    try {
      // Get all learning paths with applied filters
      const data = await this.getLearningPathPerformanceList({
        ...filters,
        limit: 1000 // Get maximum records for export
      });

      // Convert to CSV format
      if (format === 'csv') {
        const csvContent = this.convertToCSV(data.learning_paths);
        return new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      }

      // For Excel format, would need additional library like xlsx
      throw new Error('Excel export not yet implemented');
    } catch (error) {
      console.error('Error exporting learning path performance:', error);
      throw error;
    }
  }

  /**
   * Export learning path users to CSV format
   * @param learningPathId - The unique identifier of the learning path
   * @param filters - Optional filters to apply
   * @returns Promise with export data
   */
  async exportLearningPathUsersToCSV(
    learningPathId: string,
    filters?: Omit<LearningPathUsersListParams, 'page' | 'limit'>
  ): Promise<Blob> {
    try {
      const response = await this.getLearningPathUsers(learningPathId, {
        ...filters,
        page: 1,
        limit: 10000, // Fetch up to 10,000 users for the export
      });

      if (!response.data) {
        throw new Error(`Could not find learning path data for ID: ${learningPathId}`);
      }
      const users = response.data.users;

      if (!users || users.length === 0) {
        throw new Error("No user data is available to export.");
      }
      const csvContent = this.convertUsersToCSV(users);

      return new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    } catch (error) {
      console.error(`Error exporting users for learning path ID ${learningPathId}:`, error);
      throw error;
    }
  }

  /**
   * Convert USER data to CSV format.
   * @param users - Array of learning path user data.
   * @returns A string formatted as a CSV.
   */
  private convertUsersToCSV(users: LearningPathUserData[]): string {
    const headers = [
      'User ID',
      'First Name',
      'Last Name',
      'Email',
      'Status',
      'Progress Percentage',
      'Time Spent (Seconds)',
    ];

    // Helper to safely handle data that might contain commas or quotes.
    const escapeCsvCell = (cellData: string | number) => {
      const stringData = String(cellData);
      if (stringData.includes(',') || stringData.includes('"') || stringData.includes('\n')) {
        return `"${stringData.replace(/"/g, '""')}"`;
      }
      return stringData;
    };

    const rows = users.map(user => [
      escapeCsvCell(user.user_id),
      escapeCsvCell(user.given_name),
      escapeCsvCell(user.family_name),
      escapeCsvCell(user.email),
      escapeCsvCell(user.status.replace('_', ' ')),
      escapeCsvCell(user.progress_percentage),
      escapeCsvCell(user.total_time_spent_seconds),
    ].join(','));

    return [headers.join(','), ...rows].join('\n');
  }

  /**
   * Convert learning path data to CSV format
   * @param learningPaths - Array of learning path performance data
   * @returns CSV string
   */
  private convertToCSV(learningPaths: LearningPathPerformanceItemDto[]): string {
    const headers = [
      'Learning Path ID',
      'Learning Path Title',
      'Status',
      'Total Enrollments',
      'Active Enrollments',
      'Completed Users',
      'Completion Rate (%)',
      'Total Courses'
    ];

    const rows = learningPaths.map(path => [
      path.learning_path_id,
      `"${path.learning_path_title}"`, // Quote to handle commas in titles
      path.status,
      path.total_user_enrollments,
      path.total_user_active_enrollments,
      path.completed_users,
      path.completion_percentage.toFixed(2),
      path.total_courses
    ]);

    return [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
  }
  async updateTrackingSettings(trackingId: string, data: {
    is_mandatory?: boolean;
    deadline_date?: string;
    send_notification?: boolean;
    enable_automatic_reminder?: boolean;
    reminder_frequency_days?: number | null;
  }) {
    const response = await apiClient.put(API_ENDPOINTS.LEARNING_PATH_PERFORMANCE.UPDATE_SETTINGS(trackingId), data);
    return response.data?.data || response.data;
  }

  async revokeLearningPathForUser(learningPathId: string, userId: string) {
    try {
      const response = await apiClient.delete(
        API_ENDPOINTS.LEARNING_PATH_PERFORMANCE.REVOKE(learningPathId, userId)
      );
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to revoke learning path');
    } catch (error: any) {
      throw error;
    }
  }
}

// Export singleton instance
export const learningPathPerformanceService = new LearningPathPerformanceService();
